import React, { useReducer, useEffect, useMemo } from 'react';
import createNetworkingClient from 'utils/networking-client';
import { trackV2 } from 'utils/analytics';
import { logError } from '../../../../../utils/sentry';
import AdyenForm from '../AdyenForm';
import { useCheckoutContext } from '../../state/CheckoutContext';
import { subscriptionType } from '../../utils/checkoutUtils';

const INITIALIZED = 'initialized';
const UNSET = 'unset';
const ACTION_SET_SESSION_SUCCESS = 'setSessionSuccess';
const ACTION_SET_SESSION_ERROR = 'setSessionError';
const ACTION_RESET_SESSION = 'resetSession';
const startSubscription = async (
  adyenResult,
  sessionId,
  recurring,
  sku,
  promotionCode,
  isPromoCodeInputVisible,
  isShowingTrial,
  analyticsData,
  isTrial,
  handleError
) => {
  const client = createNetworkingClient();
  const options = {
    method: 'POST',
    url: '/adyen/start_drop_in_subscription',
    data: {
      result_code: adyenResult.resultCode,
      adyen_session_id: sessionId,
      sku,
      recurring,
      code: promotionCode,
      organic: isPromoCodeInputVisible || !isShowingTrial
    }
  };
  client.request(options).then((response) => {
    if (response && response.status === 200) {
      trackV2({
        category: 'summit_upsell',
        page: 'cart',
        action: 'finish_payment',
        properties: {
          ...analyticsData,
          adyen_dropin: true,
          subscription_type: subscriptionType(isTrial)
        }
      });

      window.setTimeout(() => {
        window.location = response.headers.location;
      }, 2000);
    } else if (response) {
      const errorResponse = new Error(
        `Got an error response from server ${response.status}`
      );
      errorResponse.name = 'AdyenCustomError';
      handleError(errorResponse);
    } else {
      const errorNoResponse = new Error(
        'Did not get a response from axios client'
      );
      errorNoResponse.name = 'AdyenCustomError';
      handleError(errorNoResponse);
    }
  });
};
const sendSubmitTrackEvent = (analyticsData, isTrial, isStudentCheckout) => {
  const forPage = isStudentCheckout ? 'student_plan_checkout' : 'checkout';
  trackV2({
    category: 'subscriptions',
    page: forPage,
    element: 'trial_or_subscribe',
    action: 'click',
    properties: {
      ...analyticsData,
      adyen_dropin: true,
      subscription_type: subscriptionType(isTrial)
    }
  });
};
const generateSession = async (
  recurring,
  amount,
  currency,
  isPromoCodeInputVisible,
  promotionCode,
  isShowingTrial,
  dispatch
) => {
  const client = createNetworkingClient();
  const options = {
    method: 'POST',
    url: '/adyen/generate_session',
    data: {
      recurring,
      organic: isPromoCodeInputVisible || !isShowingTrial,
      code: promotionCode
    }
  };
  client
    .request(options)
    .then((response) => {
      const payload = {
        sessionId: response.data.session_id,
        sessionData: response.data.session_data,
        amount,
        currency,
        recurring
      };
      dispatch({ type: ACTION_SET_SESSION_SUCCESS, payload });
    })
    .catch(() => {
      dispatch({ type: ACTION_SET_SESSION_ERROR, payload: { recurring } });
    });
};

const initialSessionData = (
  sessionData,
  sessionId,
  duration,
  amount,
  currency,
  promotionCode,
  isPromoCodeInputVisible
) => {
  const data = {
    currency,
    promotionCode,
    isPromoCodeInputVisible,
    yearly: {
      amount: null,
      status: UNSET,
      sessionData: '',
      sessionId: ''
    },
    monthly: {
      amount: null,
      status: UNSET,
      sessionData: '',
      sessionId: ''
    }
  };

  data[duration].sessionId = sessionId;
  data[duration].sessionData = sessionData;
  data[duration].status = INITIALIZED;
  data[duration].amount = amount;

  return data;
};

const getOrigin = () => {
  try {
    if (typeof document === 'undefined') {
      return 'no document found';
    }
    return document.location.origin;
  } catch (e) {
    return 'error retrieving origin';
  }
};

const getAncestorOrigins = () => {
  try {
    if (typeof document === 'undefined') {
      return 'no document found';
    }
    const items = document.location.ancestorOrigins;
    if (!items) {
      return 'ancestor origin not supported';
    }
    if (items.length === 0) {
      return 'no ancestor origins';
    }

    const itemValues = [];
    for (let index = 0; index < items.length; index++) {
      itemValues.push(items.item(index));
    }
    return itemValues.join(',');
  } catch (e) {
    return 'error retrieving ancestor origins';
  }
};

const reducer = (state, action) => {
  switch (action.type) {
    case ACTION_SET_SESSION_SUCCESS:
      return {
        ...state,
        [action.payload.recurring]: {
          sessionData: action.payload.sessionData,
          sessionId: action.payload.sessionId,
          amount: action.payload.amount,
          status: INITIALIZED
        }
      };
    case ACTION_SET_SESSION_ERROR:
      return {
        ...state,
        [action.payload.recurring]: {
          sessionData: null,
          sessionId: null,
          amount: null,
          status: INITIALIZED
        }
      };
    case ACTION_RESET_SESSION:
      return initialSessionData(
        action.payload.sessionData,
        action.payload.sessionId,
        action.payload.duration,
        action.payload.amount,
        action.payload.currency,
        action.payload.promotionCode,
        action.payload.isPromoCodeInputVisible
      );
    default:
      throw new Error();
  }
};

const isNewSessionId = (sessionId, state) => {
  return (
    state.yearly.sessionId !== sessionId &&
    state.monthly.sessionId !== sessionId
  );
};

const createHandler = (
  sessionId,
  duration,
  sku,
  promotionCode,
  isPromoCodeInputVisible,
  isShowingTrial,
  analyticsData,
  isTrial,
  handleError
) => {
  return (adyenResult) => {
    startSubscription(
      adyenResult,
      sessionId,
      duration,
      sku,
      promotionCode,
      isPromoCodeInputVisible,
      isShowingTrial,
      analyticsData,
      isTrial,
      handleError
    );
  };
};

const createSubmitHandler = (analyticsData, isTrial, isStudentPromotion) => {
  return () => {
    sendSubmitTrackEvent(analyticsData, isTrial, isStudentPromotion);
  };
};

// AdyenSessionAdapter supports managing of internal state for fetching and minting
// new session data when the price changes. For the dropin, component we need
// to create a new session on the server that has the price and currency to use
// in concert with the dropin component.
const AdyenSessionAdapter = () => {
  const { checkoutEntityState } = useCheckoutContext();
  const {
    duration,
    sessionData,
    sessionId,
    clientKey,
    environment,
    products,
    promoCodeInputVisibility,
    isTrial,
    analyticsData,
    isStudentPromotion
  } = checkoutEntityState;
  const currency = products?.[duration]?.currency;
  const amount = products?.[duration]?.amountInMinorUnits;
  const sku = products?.[duration].sku;
  const promotionCode = products?.promotion.code;
  const isPromoCodeInputVisible = promoCodeInputVisibility;
  const isShowingTrial = isTrial;
  const [state, dispatch] = useReducer(
    reducer,
    initialSessionData(
      sessionData,
      sessionId,
      duration,
      amount,
      currency,
      promotionCode,
      isPromoCodeInputVisible
    )
  );
  const currentStatus = state[duration].status;
  const currentSessionId = state[duration].sessionId;
  const currentSessionData = state[duration].sessionData;
  const currentCurrency = state.currency;
  const currentAmount = state[duration].amount;
  const handleError = useMemo(() => {
    const withErrorLogger = (error) => {
      const fetchVersion =
        typeof window !== 'undefined' && 'fetch' in window
          ? 'native-fetch'
          : 'whatwg-fetch';
      const ancestorOrigins = getAncestorOrigins();
      const origin = getOrigin();
      const context = {
        'checkout.isTrial': isTrial,
        'checkout.isStudentPromotion': isStudentPromotion,
        'checkout.isPromoCodeInputVisible': isPromoCodeInputVisible,
        'checkout.amount': amount,
        'checkout.sessionid': currentSessionId,
        'checkout.ancestorOrigins': ancestorOrigins
      };
      const tags = {
        'checkout.source': 'adyen.dropin',
        'checkout.environment': environment,
        'checkout.sku': sku,
        'checkout.duration': duration,
        'checkout.clientkey': clientKey,
        'checkout.fetchVersion': fetchVersion,
        'checkout.origin': origin
      };
      logError(error, { contexts: { checkout: context }, tags });
    };
    return withErrorLogger;
  }, [
    environment,
    currentSessionId,
    isTrial,
    isStudentPromotion,
    isPromoCodeInputVisible,
    amount,
    sku,
    duration,
    clientKey
  ]);

  if (isNewSessionId(sessionId, state)) {
    dispatch({
      type: ACTION_RESET_SESSION,
      payload: {
        sessionData,
        sessionId,
        duration,
        amount,
        currency,
        promotionCode,
        isPromoCodeInputVisible
      }
    });
  }

  useEffect(() => {
    if (currentStatus === INITIALIZED) {
      return;
    }
    generateSession(
      duration,
      amount,
      currency,
      isPromoCodeInputVisible,
      promotionCode,
      isShowingTrial,
      dispatch
    );
  }, [duration, currentStatus, isPromoCodeInputVisible, promotionCode]);

  const completedHandler = createHandler(
    currentSessionId,
    duration,
    sku,
    promotionCode,
    isPromoCodeInputVisible,
    isShowingTrial,
    analyticsData,
    isTrial,
    handleError
  );
  const submitHandler = createSubmitHandler(
    analyticsData,
    isTrial,
    isStudentPromotion
  );

  return (
    <AdyenForm
      sessionData={currentSessionData}
      sessionId={currentSessionId}
      clientKey={clientKey}
      environment={environment}
      currency={currentCurrency}
      amount={currentAmount}
      promotionCode={promotionCode}
      handleCompleted={completedHandler}
      handleSubmit={submitHandler}
      handleError={handleError}
    />
  );
};

export default AdyenSessionAdapter;
